import { useState, useEffect } from "react";
import {
  Typography,
  Dialog,
  Button,
  Accordion,
  AccordionHeader,
  AccordionBody,
  Card,
  Input,
} from "@material-tailwind/react";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { t } from "i18next";
import { useWorkorderHose } from "../../../hooks/hoses/useWorkorderHoses";
import { useWorkorderContext } from "../../../context/WorkorderContext";
import PartsSection from "../../../views/workorder/tabs/machines-parts/sections/PartsSection";
import { toast } from "react-hot-toast";
import AccordionTitleChevron from "../../copy-button/AccordionTitleChevron";
import CopyToClipboardButton from "../../copy-button/CopyToClipboardButton";

interface HoseItemProps {
  workorderHose: EasyTech.WorkorderHose;
  workorderId: string;
  machineId: string;
  onRemoveHose: (hoseId: string) => void;
  canEdit: boolean;
}

/**
 * Composant pour afficher un flexible individuel avec ses détails
 * Permet l'édition, la suppression et l'ajout de pièces
 *
 * @param workorderHose - Flexible à afficher
 * @param workorderId - ID du workorder
 * @param machineId - ID de la machine associée (si applicable)
 * @param onRemoveHose - Fonction pour supprimer le flexible
 */
export default function HoseItem({
  workorderHose,
  workorderId,
  machineId,
  onRemoveHose,
  canEdit,
}: HoseItemProps) {
  const [localHose, setLocalHose] = useState({
    ...workorderHose,
    parts: workorderHose.parts || [],
  });

  const { workorder, setInlineAdd, inlineAdd, setCurrentEditContext, editingPartId } =
    useWorkorderContext();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(workorderHose.name || "");
  const [editedBarcode, setEditedBarcode] = useState(
    workorderHose.barCode || ""
  );
  const [editedQrCode, setEditedQrCode] = useState(
    workorderHose.qrCode || ""
  );
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(true);
  const [isDeleted, setIsDeleted] = useState(false);

  const { editHose } = useWorkorderHose(workorderId);

  /**
   * Gère l'ajout d'une pièce au flexible
   * Définit le contexte d'édition et active l'ajout inline
   */
  const handleAddPart = () => {
    setOpenAccordion(true);
    // Définir le contexte d'édition pour ce hose spécifique
    setCurrentEditContext(`hose-${workorderHose.id}`);
    setInlineAdd(!inlineAdd);
  };

  /**
   * Met à jour l'état local lorsque le hose est modifié dans le contexte
   */
  useEffect(() => {
    const updatedHose = workorder?.hoses?.find(
      (h) => h.id === workorderHose.id
    );
    if (updatedHose) {
      setLocalHose({
        ...updatedHose,
        parts: updatedHose.parts || [],
      });
    }
  }, [workorder, workorderHose.id]);

  /**
   * Save des modifications du hose
   */
  const handleSaveEdit = async () => {
    try {
      const updatedHoseData = {
        ...localHose,
        name: editedName,
        //barCode: editedBarcode,
        qrCode: editedQrCode,
      };

      const updated = await editHose(localHose.id, updatedHoseData);

      setLocalHose(updated as EasyTech.WorkorderHose);
      setIsEditing(false);
      toast.success(t("workorder:success:hoseUpdated"));
    } catch (error) {
      console.error(t("workorder:errors:updateHose"), error);
      toast.error(t("workorder:errors:updateHose"));
    }
  };

  /**
   * Ferme le dialogue de confirmation sans supprimer
   */
  const handleCancelDelete = () => {
    setIsConfirmDialogOpen(false);
  };

  /**
   * Confirme et exécute la suppression du hose
   */
  const handleConfirmDelete = async () => {
    try {
      await onRemoveHose(workorderHose.id);
      setIsDeleted(true);
      setIsConfirmDialogOpen(false);
      toast.success(t("workorder:success:hoseRemoved"));
    } catch (error) {
      console.error(t("workorder:errors:removeHose"), error);
      toast.error(t("workorder:errors:removeHose"));
      setIsConfirmDialogOpen(false);
    }
  };

  /**
   * Annule l'édition et restaure les valeurs initiales
   */
  const handleCancelEdit = () => {
    setEditedName(localHose.name || "");
    setEditedBarcode(localHose.barCode || "");
    setEditedQrCode(localHose.qrCode || "");
    setIsEditing(false);
  };

  if (isDeleted) return null;

  const hoseName = localHose.name || t("workorder:hoses:unnamedHose");
  const hoseCopyText = localHose.qrCode ? `${hoseName} ${localHose.qrCode}` : hoseName;

  return (
    <Card className="bg-transparent shadow-none">
      <Accordion open={openAccordion} className={isDeleted ? "hidden" : ""}>
        <AccordionHeader className="font-medium text-gray-900 text-md flex items-center [&>span]:hidden">
          <div className="flex w-full justify-between items-center">
            <div className="flex items-center gap-2">
              {isEditing ? (
                <div className="p-4 flex gap-2">
                  <Typography variant="small" className="mb-2 font-medium">
                    {t("workorder:hoses:name")}
                  </Typography>
                  <Input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    placeholder={t("workorder:hoses:name")}
                    className="bg-white mb-4"
                  />
                  <Typography variant="small" className="mb-2 font-medium">
                    {t("workorder:hoses:qrCode")}
                  </Typography>
                  <Input
                    type="text"
                    value={editedQrCode}
                    onChange={(e) => setEditedQrCode(e.target.value)}
                    placeholder={t("workorder:hoses:qrCode")}
                    className="bg-white"
                  />
                </div>
              ) : (
                <div
                  className="flex gap-2 items-center cursor-pointer"
                  onClick={() => setOpenAccordion(!openAccordion)}
                >
                  <AccordionTitleChevron open={openAccordion} />
                  <Typography
                    variant="h5"
                    className="font-medium text-gray-900 uppercase"
                  >
                    {hoseName}
                  </Typography>
                  {localHose.qrCode && (
                    <Typography variant="paragraph" className="text-sm text-gray-500">
                      {localHose.qrCode}
                    </Typography>
                  )}
                  <CopyToClipboardButton text={hoseCopyText} />
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isEditing ? (
                <div className="flex gap-2 items-center">
                  
                  <Button
                    variant="filled"
                    size="md"
                    color="white"
                    className="btn-cancel normal-case font-thin border"
                    onClick={handleCancelEdit}
                      disabled={!canEdit}
                    >
                      {t("workorder:hoses:cancel")}
                    </Button>
                  <Button
                    variant="filled"
                    className="bg-blue-500 normal-case font-thin"
                    onClick={handleSaveEdit}
                    disabled={!canEdit}
                    >
                      {t("workorder:hoses:save")}
                    </Button>
                </div>
              ) : (
                <>
                  <Button
                    variant="filled"
                    color="white"
                    className="normal-case font-thin border border-gray-300"
                      onClick={() => {
                        setIsConfirmDialogOpen(true);
                      }}
                      disabled={!canEdit}
                    >
                      {t("workorder:hoses:removeHose")}
                    </Button>
                  <Button
                    variant="filled"
                    className="bg-blue-500 normal-case font-thin"
                    onClick={() => setIsEditing(true)}
                    disabled={!canEdit}
                    >
                      {t("workorder:hoses:editHose")}
                    </Button>
                  <Button
                    onClick={handleAddPart}
                    variant="filled"
                    size="md"
                    className="flex items-center gap-2 bg-blue-50 shadow-md text-sm text-blue-500 px-3 rounded-lg font-thin normal-case"
                    disabled={!canEdit || !!editingPartId}
                  >
                    <PlusCircleIcon className="h-4 w-4" />
                    {t("workorder:addPart")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </AccordionHeader>
        <AccordionBody className="py-4">
          <div className="parts-container">
            <PartsSection
              workorderId={workorderId}
              hoseId={workorderHose.id}
              machineId={machineId || workorderHose.machine?.id || ""}
              targetType="hose"
              canEdit={canEdit}
            />
          </div>
        </AccordionBody>
      </Accordion>

      {/* Dialogue de confirmation pour la suppression */}
      <Dialog
        open={isConfirmDialogOpen}
        handler={handleCancelDelete}
        size="xs"
      >
        <div className="p-4">
          <Typography variant="h5" className="mb-4">
            {t("workorder:hoses:confirmRemoveHose")}
          </Typography>
          <Typography
            variant="paragraph"
            className="mb-6 text-gray-700"
          >
            {t("workorder:hoses:confirmRemoveHoseMessage")}
          </Typography>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outlined"
              color="red"
              onClick={handleCancelDelete}
            >
              {t("workorder:hoses:cancel")}
            </Button>
            <Button
              variant="filled"
              color="red"
              onClick={handleConfirmDelete}
            >
              {t("workorder:hoses:removeHose")}
            </Button>
          </div>
        </div>
      </Dialog>
    </Card>
  );
}
